import { Box, InputLabel, MultiSelectMenu, OptionType, Text } from '@fullstory/components';
import { IssuesBanner, SnoozeUpdate } from '@fullstory/dqm-ui';
import { Event } from '@fullstory/proto/fs/api/segment';
import { APIEventIssueSilence, Issues } from '@fullstory/proto/fs/services/dx-objects';
import { SpecObjectInfo } from '@fullstory/redux';
import { AppearancesContext, AppearancesProps } from '@fullstory/signal-studio-context';
import { useDefaultAppearancesPropertyOptions } from '@fullstory/signal-studio-context';
import React, { useContext, useMemo } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { APIEvent, PropertyRow } from '../../data-mgmt';
import { APIEventDefinition } from '../APIEventDefinition';
import { DetailModalPropertiesTable } from '../DetailModalPropertiesTable';

const APIEventAppearances = ({
  Appearances,
  event,
}: {
  Appearances: React.ComponentType<AppearancesProps>;
  event: Event;
}) => {
  const intl = useIntl();
  const appearancesPropertyOptions = useDefaultAppearancesPropertyOptions();

  return (
    <Appearances
      data-component="APIEventAppearances"
      event={event}
      propertyOptions={appearancesPropertyOptions}
      objectType={intl.formatMessage({ defaultMessage: 'event', id: 'bHpXg4' })}
    />
  );
};

interface APIEventDetailsProps {
  apiEvent: APIEvent;
  issues: Issues.Issue[];
  snoozed: APIEventIssueSilence | null;
  setSnoozed?: (update: SnoozeUpdate) => void;
  eventObjectType: string;
  // The following will be optional as they are not used in the config in SignalStudio.
  propertiesProps?: {
    properties: PropertyRow[];
    propertiesLoading: boolean;
    propertiesFailed: boolean;
    getConnections: () => void;
    canArchiveProperties: boolean;
  };
  header?: string;
  selectedFsSpecObjectSlugs: OptionType<string>[];
  setSelectedFsSpecObjectSlugs: (slugs: OptionType<string>[]) => void;
  fsSpecEnabled: boolean;
  fsSpecRoleOptions: OptionType<string>[];
  existingSpecObjects: SpecObjectInfo[];
  preventOverflow?: boolean;
}

export const APIEventDetails: React.FC<APIEventDetailsProps> = ({
  apiEvent,
  issues,
  snoozed,
  setSnoozed,
  eventObjectType,
  propertiesProps,
  header,
  selectedFsSpecObjectSlugs,
  setSelectedFsSpecObjectSlugs,
  fsSpecEnabled,
  fsSpecRoleOptions,
  existingSpecObjects,
  preventOverflow,
}) => {
  const appearances = useContext(AppearancesContext);
  const { Appearances } = { ...appearances };

  const event = useMemo(
    () => ({
      primary: {
        custom: {
          named: {
            name: apiEvent.name,
          },
        },
      },
      deps: [],
    }),
    [apiEvent.name],
  );

  return (
    <>
      {header && (
        <Box marginBottom={20}>
          <Text appearance="heading1">{header}</Text>
        </Box>
      )}
      {(issues?.length || snoozed) && setSnoozed && (
        <Box marginBottom={8}>
          <IssuesBanner
            snoozed={snoozed || null}
            setSnooze={setSnoozed}
            objectType={eventObjectType}
          />
        </Box>
      )}
      {fsSpecEnabled && fsSpecRoleOptions.length > 0 && (
        <Box marginBottom={20} testId="fs-spec-role-select">
          <InputLabel id="Role" optional>
            <FormattedMessage defaultMessage="Role" id="1ZgrhW" />
          </InputLabel>
          <MultiSelectMenu
            menuHeight={385}
            variant="fill-builder"
            options={fsSpecRoleOptions}
            onChange={setSelectedFsSpecObjectSlugs}
            selected={selectedFsSpecObjectSlugs}
            filterable
            testId="api-event-modal-role-multi-select"
            preventOverflow={preventOverflow ? 16 : undefined}
            flipY={preventOverflow || undefined}
          />
        </Box>
      )}
      <APIEventDefinition apiEventName={apiEvent.name} />
      {Appearances && (
        <Box marginTop={20} marginBottom={20}>
          <APIEventAppearances Appearances={Appearances} event={event} />
        </Box>
      )}
      {propertiesProps && (
        <Box>
          <DetailModalPropertiesTable
            properties={propertiesProps.properties}
            connectionParent={apiEvent.name}
            loading={propertiesProps.propertiesLoading}
            failed={propertiesProps.propertiesFailed}
            retryFailed={propertiesProps.getConnections}
            canArchive={propertiesProps.canArchiveProperties}
            existingSpecObjects={existingSpecObjects}
          />
        </Box>
      )}
    </>
  );
};
